import React, { useEffect, useState } from 'react';
import { Table, Button, Space, Card, Modal, Form, Input, Select, message, Typography, Tag, Popconfirm, Row, Col, Upload, Grid } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined, DownloadOutlined } from '@ant-design/icons';
import client from '../api/client';
import { useAuth } from '../context/AuthContext';

const { Title } = Typography;
const { Option } = Select;

const ChartOfAccountsPage = () => {
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const { user } = useAuth();
    const isAdmin = user?.role === 'ADMIN';
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isImportVisible, setIsImportVisible] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [importLoading, setImportLoading] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [form] = Form.useForm();

    const fetchAccounts = async () => {
        setLoading(true);
        try {
            const { data } = await client.get('/master/coa');
            setAccounts(data);
        } catch (error) {
            message.error('Failed to fetch chart of accounts');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAccounts();
    }, []);

    const handleAdd = () => {
        setEditingId(null);
        form.resetFields();
        setIsModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingId(record.id);
        form.setFieldsValue({
            ...record,
            parent_id: record.parent_id || record.parent?.id,
        });
        setIsModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            await client.delete(`/master/coa/${id}`);
            message.success('Account deleted successfully');
            fetchAccounts();
        } catch (error) {
            message.error('Failed to delete account');
        }
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
    };

    const handleImport = async () => {
        if (!importFile) {
            message.error('Please select a CSV file to import.');
            return;
        }
        setImportLoading(true);
        try {
            const formData = new FormData();
            formData.append('file', importFile);
            const { data } = await client.post('/master/coa/import', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            const errorCount = data?.errors?.length || 0;
            const summary = `Imported: ${data?.created || 0} created, ${data?.updated || 0} updated`;
            message.success(errorCount ? `${summary} (${errorCount} errors)` : summary);
            setIsImportVisible(false);
            setImportFile(null);
            fetchAccounts();
        } catch (error) {
            message.error(error.response?.data?.message || 'Failed to import accounts');
        } finally {
            setImportLoading(false);
        }
    };

    const handleDownloadTemplate = () => {
        const templateRows = [
            ['code', 'name', 'type', 'parent_code', 'is_active'],
            ['1000', 'Cash on Hand', 'ASSET', '', '1'],
            ['2000', 'Accounts Payable', 'LIABILITY', '', '1'],
            ['3000', 'Owner Equity', 'EQUITY', '', '1'],
            ['4000', 'Sales Revenue', 'REVENUE', '', '1'],
            ['5000', 'Operating Expenses', 'EXPENSE', '', '1'],
            ['5100', 'Salaries Expense', 'EXPENSE', '5000', '1'],
        ];
        const csvContent = templateRows
            .map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(','))
            .join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'chart_of_accounts_template.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const onFinish = async (values) => {
        try {
            if (editingId) {
                await client.put(`/master/coa/${editingId}`, values);
                message.success('Account updated successfully');
            } else {
                await client.post('/master/coa', values);
                message.success('Account created successfully');
            }
            setIsModalVisible(false);
            fetchAccounts();
        } catch (error) {
            const data = error.response?.data;
            const validationErrors = data?.errors ? Object.values(data.errors).flat().join(' ') : '';
            message.error(data?.message || validationErrors || 'Operation failed');
        }
    };

    const columns = [
        {
            title: 'Account Code',
            dataIndex: 'code',
            key: 'code',
            render: (text) => <Typography.Text strong style={{ color: 'var(--text-primary)' }}>{text}</Typography.Text>
        },
        {
            title: 'Account Name',
            dataIndex: 'name',
            key: 'name',
            render: (text) => <Typography.Text strong style={{ color: 'var(--text-primary)' }}>{text}</Typography.Text>
        },
        {
            title: 'Account Type',
            dataIndex: 'type',
            key: 'type',
            render: (type) => {
                let color = 'default';
                if (type === 'ASSET') color = 'blue';
                if (type === 'LIABILITY') color = 'red';
                if (type === 'EQUITY') color = 'gold';
                if (type === 'REVENUE') color = 'green';
                if (type === 'EXPENSE') color = 'orange';
                return <Tag color={color} style={{ borderRadius: 12, padding: '0 12px' }}>{type}</Tag>;
            }
        },
        {
            title: 'Parent Group',
            dataIndex: 'parent',
            key: 'parent',
            render: (parent) => parent?.name ? <Tag color="default" style={{ borderRadius: 12 }}>{parent.name}</Tag> : '-',
        },
        {
            title: 'Status',
            dataIndex: 'is_active',
            key: 'is_active',
            render: (isActive) => (
                <Tag color={isActive ? 'success' : 'error'} style={{ borderRadius: 12 }}>
                    {isActive ? 'Active' : 'Inactive'}
                </Tag>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            align: 'right',
            render: (_, record) => (
                <Space size="small">
                    <Button
                        type="text"
                        icon={<EditOutlined style={{ color: '#3b82f6' }} />}
                        onClick={() => handleEdit(record)}
                        className="action-btn"
                    />
                    <Popconfirm
                        title="Delete Account"
                        description="Are you sure you want to delete this account?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            type="text"
                            icon={<DeleteOutlined style={{ color: '#ef4444' }} />}
                            danger
                            className="action-btn"
                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ].filter(col => col.key !== 'actions' || isAdmin);

    const StatCard = ({ title, value, icon, color, subText }) => (
        <Card className="premium-card" bodyStyle={{ padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <Typography.Text style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</Typography.Text>
                    <Title level={3} style={{ margin: 0, fontWeight: 600, letterSpacing: '-0.5px', color: 'var(--text-primary)' }}>
                        {value}
                    </Title>
                    {subText && <Typography.Text style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, display: 'block' }}>{subText}</Typography.Text>}
                </div>
                <div className="stat-icon" style={{ backgroundColor: `${color}15`, color: color }}>
                    {icon}
                </div>
            </div>
        </Card>
    );

    const filtered = accounts.filter((a) => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return true;
        return (
            (a.code || '').toLowerCase().includes(term) ||
            (a.name || '').toLowerCase().includes(term) ||
            (a.type || '').toLowerCase().includes(term)
        );
    });

    const stats = {
        total: filtered.length,
        revenue: (filtered || []).filter(a => a.type === 'REVENUE').length,
        expense: (filtered || []).filter(a => a.type === 'EXPENSE').length,
    };

    return (
        <div style={{ padding: isMobile ? '16px' : '24px 32px' }}>
            <div style={{ marginBottom: 32, display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 12 : 0, justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'flex-end' }}>
                <div>
                    <Title level={2} style={{ margin: 0, fontWeight: 800, color: 'var(--text-primary)' }}>Chart of Accounts</Title>
                    <Typography.Text style={{ color: 'var(--text-secondary)', fontSize: 16 }}>Manage your financial structure and ledger accounts.</Typography.Text>
                </div>
                {isAdmin && (
                    <Space>
                        <Button
                            icon={<UploadOutlined />}
                            size="large"
                            onClick={() => setIsImportVisible(true)}
                            style={{ height: 45, borderRadius: 10, fontWeight: 600, padding: '0 20px' }}
                        >
                            Import CSV
                        </Button>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            size="large"
                            onClick={handleAdd}
                            style={{ height: 45, borderRadius: 10, fontWeight: 600, padding: '0 24px' }}
                        >
                            Add Account
                        </Button>
                    </Space>
                )}
            </div>

            <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
                <Col xs={24} sm={8}>
                    <StatCard
                        title="Total Accounts"
                        value={stats.total}
                        icon={<i className="fa-solid fa-file-invoice-dollar"></i>}
                        color="#3b82f6"
                        subText="Consolidated ledger"
                    />
                </Col>
                <Col xs={24} sm={8}>
                    <StatCard
                        title="Revenue Accounts"
                        value={stats.revenue}
                        icon={<i className="fa-solid fa-chart-line"></i>}
                        color="#10b981"
                        subText="Income streams"
                    />
                </Col>
                <Col xs={24} sm={8}>
                    <StatCard
                        title="Expense Accounts"
                        value={stats.expense}
                        icon={<i className="fa-solid fa-receipt"></i>}
                        color="#f59e0b"
                        subText="Operational costs"
                    />
                </Col>
            </Row>

            <Card className="premium-card" bodyStyle={{ padding: 0 }}>
                <div style={{ padding: 16, display: 'flex', justifyContent: 'flex-end', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 12 : 0 }}>
                    <Input
                        placeholder="Search code, name, type..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ width: isMobile ? '100%' : 320, borderRadius: 10, height: 40 }}
                    />
                </div>
                <Table
                    columns={columns}
                    dataSource={filtered}
                    rowKey="id"
                    loading={loading}
                    size={isMobile ? 'small' : 'middle'}
                    scroll={isMobile ? { x: 900 } : undefined}
                    pagination={{ defaultPageSize: 12, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
                />
            </Card>

            <Modal
                title={editingId ? 'Edit Account' : 'Create New Ledger Account'}
                open={isModalVisible}
                onCancel={handleCancel}
                footer={null}
                width={500}
                className="premium-modal"
            >
                <Form form={form} layout="vertical" onFinish={onFinish}>
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="code" label="Code" rules={[{ required: true, message: 'Code is required' }]}>
                                <Input placeholder="1000" style={{ height: 40, borderRadius: 8 }} />
                            </Form.Item>
                        </Col>
                        <Col span={16}>
                            <Form.Item name="name" label="Account Name" rules={[{ required: true, message: 'Name is required' }]}>
                                <Input style={{ height: 40, borderRadius: 8 }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="type" label="Account Type" rules={[{ required: true, message: 'Type is required' }]}>
                        <Select style={{ height: 40 }}>
                            <Option value="ASSET">Asset</Option>
                            <Option value="LIABILITY">Liability</Option>
                            <Option value="EQUITY">Equity</Option>
                            <Option value="REVENUE">Revenue</Option>
                            <Option value="EXPENSE">Expense</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item name="parent_id" label="Parent Account / Group">
                        <Select allowClear placeholder="Select parent account" style={{ height: 40 }}>
                            {(accounts || [])
                                .filter((account) => account.id !== editingId)
                                .map((account) => (
                                    <Option key={account.id} value={account.id}>
                                        {account.code} - {account.name}
                                    </Option>
                                ))}
                        </Select>
                    </Form.Item>

                    <Form.Item name="is_active" label="Status">
                        <Select style={{ height: 40 }}>
                            <Option value={true}>Active</Option>
                            <Option value={false}>Inactive</Option>
                        </Select>
                    </Form.Item>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24 }}>
                        <Button onClick={handleCancel} style={{ height: 40, borderRadius: 8, padding: '0 24px' }}>Cancel</Button>
                        <Button type="primary" htmlType="submit" style={{ height: 40, borderRadius: 8, padding: '0 24px', fontWeight: 600 }}>
                            {editingId ? 'Save Changes' : 'Create Account'}
                        </Button>
                    </div>
                </Form>
            </Modal>

            <Modal
                title="Import Chart of Accounts (CSV)"
                open={isImportVisible}
                onCancel={() => { setIsImportVisible(false); setImportFile(null); }}
                onOk={handleImport}
                confirmLoading={importLoading}
                okText="Import"
                okButtonProps={{ style: { height: 40, borderRadius: 8, fontWeight: 600 } }}
                cancelButtonProps={{ style: { height: 40, borderRadius: 8 } }}
            >
                <Typography.Text style={{ display: 'block', marginBottom: 12 }}>
                    Expected columns: code, name, type, parent_code, is_active
                </Typography.Text>
                <Space wrap>
                    <Upload
                        accept=".csv,text/csv"
                        beforeUpload={(file) => {
                            setImportFile(file);
                            return false;
                        }}
                        onRemove={() => setImportFile(null)}
                        maxCount={1}
                    >
                        <Button icon={<UploadOutlined />}>Choose CSV File</Button>
                    </Upload>
                    <Button icon={<DownloadOutlined />} onClick={handleDownloadTemplate}>
                        Download Template
                    </Button>
                </Space>
                {importFile && (
                    <Typography.Text style={{ display: 'block', marginTop: 8 }} type="secondary">
                        Selected: {importFile.name}
                    </Typography.Text>
                )}
            </Modal>
        </div>
    );
};

export default ChartOfAccountsPage;




